import { useCallback } from "react";
import type { NoticeVariant } from "./Notice";
import { useNoticeContext } from "./NoticeProvider";

export interface NoticeOptions {
  variant?: NoticeVariant;
  duration?: number;
  dismissible?: boolean;
}

/**
 * useNotice
 *
 * Hook to display notices from anywhere in the component tree.
 * Must be used within a NoticeProvider.
 *
 * @example
 * ```tsx
 * const notice = useNotice();
 *
 * // Show different types of notices
 * notice.success("Settings saved!");
 * notice.error("Failed to save settings");
 * notice.warning("Your session will expire soon");
 * notice.info("New features are available");
 *
 * // With custom options
 * notice.show("Custom notice", {
 *   variant: "success",
 *   duration: 10000,
 *   dismissible: false
 * });
 *
 * // Clear all notices
 * notice.clear();
 * ```
 */
export function useNotice() {
  const { addNotice, removeNotice, clearNotices } = useNoticeContext();

  const show = useCallback(
    (message: string, options?: NoticeOptions) => {
      return addNotice(message, options);
    },
    [addNotice]
  );

  const success = useCallback(
    (message: string, options?: Omit<NoticeOptions, "variant">) => {
      return addNotice(message, { ...options, variant: "success" });
    },
    [addNotice]
  );

  const error = useCallback(
    (message: string, options?: Omit<NoticeOptions, "variant">) => {
      return addNotice(message, { ...options, variant: "error" });
    },
    [addNotice]
  );

  const warning = useCallback(
    (message: string, options?: Omit<NoticeOptions, "variant">) => {
      return addNotice(message, { ...options, variant: "warning" });
    },
    [addNotice]
  );

  const info = useCallback(
    (message: string, options?: Omit<NoticeOptions, "variant">) => {
      return addNotice(message, { ...options, variant: "info" });
    },
    [addNotice]
  );

  return {
    show,
    success,
    error,
    warning,
    info,
    dismiss: removeNotice,
    clear: clearNotices,
  };
}
